import { useEffect, useMemo, useRef } from 'react';
import { MdAdd, MdClose, MdDelete, MdOutlineDone } from 'react-icons/md';

import { CommanderActionTypesEnum } from '@spazious/dollhouse-commander';
import { useSelection } from '@spazious/dollhouse-engine';
import {
  FNode,
  FNodeTypesEnum,
  getNodeFromObject,
  MeasurementNode,
} from '@spazious/dollhouse-fsetup';
import { FShallowNodeTypesEnum } from '@spazious/dollhouse-shallow-graph';
import { useGraph, useStaging, useStore } from '@spazious/dollhouse-store';
import { ControlsModeEnum, MeasurementStackEnum } from '@spazious/dollhouse-types';
import { Button, Stack, StackProps } from '@spazious/dollhouse-uikit';
import { useEventCallback } from '@spazious/react-hooks';

export interface MeasurementToolControlsProps extends StackProps {}

export const MeasurementToolControls: React.FC<MeasurementToolControlsProps> = (props) => {
  const { selected, select } = useSelection();
  const { measurements, tool, set } = useStore();
  const layers = useGraph().graph.layers;
  const { createAction, staging } = useStaging();
  const layersRef = useRef(layers);

  useGraph();
  let nodes: MeasurementNode[] = useMemo(() => {
    return selected.reduce((acc, object) => {
      const graphNode = getNodeFromObject(object) as FNode | null;

      return graphNode?.type === FNodeTypesEnum.Measurement ? [...acc, graphNode] : acc;
    }, [] as MeasurementNode[]);
  }, [selected]);

  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  useEffect(() => {
    // disable layer when unmounting
    return () => {
      layersRef.current?.disable(FShallowNodeTypesEnum.Measurement);
    };
  }, []);

  useEffect(() => {
    if (!layers.isEnabled(FShallowNodeTypesEnum.Measurement)) {
      layers.enable(FShallowNodeTypesEnum.Measurement);
    }
  }, [layers]);

  useEffect(() => {
    if (measurements.drawEnabled) return;

    if (nodes.length) {
      set((prevState) => ({
        measurements: { ...prevState.measurements, stack: MeasurementStackEnum.Delete },
      }));
    } else {
      set((prevState) => ({
        measurements: { ...prevState.measurements, stack: MeasurementStackEnum.Initial },
      }));
    }
  }, [nodes, selected, set, tool, measurements.drawEnabled]);

  const doDelete = useEventCallback(() => {
    staging.prepare();

    nodes.forEach((node) => {
      staging.add(createAction('remove', node.selector!));
    });

    staging.commit(CommanderActionTypesEnum.Remove);
    select([]);
  });

  return (
    <Stack {...props} justifyContent="center">
      {(measurements.stack === MeasurementStackEnum.Initial ||
        measurements.stack === MeasurementStackEnum.Remove ||
        measurements.stack === MeasurementStackEnum.Done) && (
        <Button
          size="extraSmall"
          style={{ cursor: 'pointer' }}
          onClick={() =>
            set({
              controls: ControlsModeEnum.Zenithal,
              measurements: { drawEnabled: true, stack: MeasurementStackEnum.FirstPoint },
            })
          }
        >
          <MdAdd />
        </Button>
      )}
      {measurements.stack === MeasurementStackEnum.MultiplePoint && (
        <Button
          size="extraSmall"
          style={{ cursor: 'pointer' }}
          onClick={() => {
            set({
              measurements: { ...measurements, stack: MeasurementStackEnum.Done },
            });
          }}
        >
          <MdOutlineDone />
        </Button>
      )}
      {measurements.stack === MeasurementStackEnum.FirstPoint && (
        <Button
          size="extraSmall"
          style={{ cursor: 'pointer' }}
          onClick={() =>
            set({
              measurements: { drawEnabled: false, stack: MeasurementStackEnum.Initial },
            })
          }
        >
          <MdClose />
        </Button>
      )}
      {(measurements.stack === MeasurementStackEnum.MultiplePoint ||
        measurements.stack === MeasurementStackEnum.Delete) && (
        <Button
          size="extraSmall"
          style={{ cursor: 'pointer' }}
          onPointerUp={(evt) => {
            evt.stopPropagation();
            if (measurements.drawEnabled) {
              set({
                measurements: { ...measurements, stack: MeasurementStackEnum.Remove },
              });
            } else {
              doDelete();
            }
          }}
        >
          <MdDelete />
        </Button>
      )}
    </Stack>
  );
};
